/**
 * The blood on the screen, as numbers.
 *
 * A mark here is not a particle: there is no emitter, no pool, and nothing
 * that accumulates. Fourteen marks are thrown once, at load, and each one is a
 * pure function of a single number - how long ago the pane went. Ask where
 * mark `i` is 1.2 s in and you get the same answer on every machine, at every
 * frame rate, however long the tab spent in the background.
 *
 * That is the same trick `Shatter.ts` and `Plume.ts` use, and it is here for
 * the reason it is there: the window is broken in the first seconds of a run,
 * and a run is restarted far more often than it is finished. Anything that
 * accumulated would have to be torn down on every `R`. A closed form cannot
 * leak, and resetting it is passing `null`.
 *
 * The life is `GLASS_PAIN.duration` rather than a number of its own, because
 * what the blood is *for* is showing how much of the slow is left. See the
 * `BLOOD` block in src/config/gameplay.ts.
 *
 * No DOM in here, and no three.js: `BloodOverlay` owns the canvas, this owns
 * what goes on it. Same split as Plume and Shatter, and for the same reason -
 * it can be sampled ten thousand times in a test with no canvas anywhere near
 * it.
 */

import { BLOOD, GLASS_PAIN } from '../config/gameplay';
import { lerp } from '../utils/math';

/** Seconds the blood is up: exactly as long as the legs are gone. */
export const BLOOD_DURATION = GLASS_PAIN.duration;

/** One speck thrown off a mark, in the mark's own units. */
export interface SpeckSeed {
  /** Which way it was thrown, radians. Forwards off the mark, give or take. */
  angle: number;
  /** How far out it landed, in the mark's radii. */
  distance: number;
  /** Its own radius, as a fraction of the mark's. */
  radius: number;
}

/** One hump of a mark's outline: how far off round, and where. */
export interface LobeSeed {
  amplitude: number;
  phase: number;
}

/** One mark's fixed identity. Authored once, at load, never stepped. */
export interface SplatSeed {
  /** Where it landed. `x` is a fraction of the screen's width, `y` of its height. */
  x: number;
  y: number;
  /** Radius as a fraction of the screen's height. */
  radius: number;
  /** How solid it is at its fullest. */
  alpha: number;
  /** Seconds after the break that it arrives. */
  delay: number;
  /** Which way it was going when it hit, radians. Its tail points back up it. */
  heading: number;
  /** How much longer it is along that heading than across it. */
  stretch: number;
  /** The spike off its trailing end, in radii. */
  tail: number;
  /** How far down the screen it runs over the whole life, in height fractions. */
  run: number;
  lobes: LobeSeed[];
  specks: SpeckSeed[];
}

/** Where a mark is this frame, and how much of it is left. */
export interface SplatPose {
  x: number;
  y: number;
  /** Height fractions, as authored - a mark does not grow. */
  radius: number;
  /** 0..1, and 0 both before it lands and after the slow has worn off. */
  opacity: number;
  /** How far it has run, in height fractions. Also the length of its trail. */
  drip: number;
}

export function splatPose(): SplatPose {
  return { x: 0, y: 0, radius: 0, opacity: 0, drip: 0 };
}

/**
 * The marks, thrown round the middle of the screen and kept out of it.
 *
 * `rng` is expected to be a seeded generator called at load time and never
 * again - the same allowance src/utils/prng.ts is written for. Nothing about
 * the splatter is random once the page has loaded, so two people watching one
 * replay watch the same window and get the same blood on it.
 *
 * Placed by angle and distance rather than by rolling an x and a y and
 * rejecting the middle: rejection sampling with a seeded generator means the
 * number of rolls depends on the rolls, which is a fine way to make a change
 * to one constant quietly reshuffle every other mark.
 */
export function splatSeeds(rng: () => number): SplatSeed[] {
  const seeds: SplatSeed[] = [];
  for (let i = 0; i < BLOOD.count; i++) {
    const angle = rng() * Math.PI * 2;
    const distance = lerp(BLOOD.keepOut, BLOOD.reach, Math.pow(rng(), BLOOD.outward));
    const radius = lerp(BLOOD.radius.min, BLOOD.radius.max, Math.pow(rng(), BLOOD.sizeBias));
    // Which way it was travelling: outwards, give or take. The whole spray is
    // one thing that happened - see `BLOOD.headingSpread`.
    const heading = angle + (rng() - 0.5) * BLOOD.headingSpread;
    // How much of the size range this one has, 0 at a speck and 1 at the
    // heaviest drop. Only the wet ones run.
    const heft = (radius - BLOOD.radius.min) / (BLOOD.radius.max - BLOOD.radius.min);

    const lobes: LobeSeed[] = [];
    for (let lobe = 0; lobe < BLOOD.lobes; lobe++) {
      // Never near zero on the first hump: a mark that rolls a round outline
      // is the one mark on the screen that reads as a sticker.
      lobes.push({
        amplitude: (BLOOD.wobble * lerp(0.4, 1, rng())) / (lobe + 1),
        phase: rng() * Math.PI * 2,
      });
    }

    const specks: SpeckSeed[] = [];
    const thrown = Math.floor(rng() * (BLOOD.satellites + 1));
    for (let speck = 0; speck < thrown; speck++) {
      specks.push({
        angle: heading + (rng() - 0.5) * BLOOD.satelliteSpread,
        distance: lerp(1.3, BLOOD.satelliteReach, rng()),
        radius: lerp(BLOOD.satelliteRadius.min, BLOOD.satelliteRadius.max, rng()),
      });
    }

    seeds.push({
      x: 0.5 + Math.cos(angle) * distance,
      y: 0.5 + Math.sin(angle) * distance,
      radius,
      alpha: lerp(BLOOD.alpha.min, BLOOD.alpha.max, rng()),
      delay: rng() * BLOOD.stagger,
      heading,
      stretch: BLOOD.stretch * lerp(0.3, 1, rng()),
      tail: BLOOD.tail * lerp(0.15, 1, rng()),
      run: BLOOD.run * heft * lerp(0.35, 1, rng()),
      lobes,
      specks,
    });
  }
  return seeds;
}

/**
 * Mark `seed`, `age` seconds after the glass went, into `out`.
 *
 * Written into a caller-owned pose rather than returned, because this runs
 * once per mark per frame and fourteen allocations a frame is a splatter that
 * shows up in a profile for no reason at all.
 *
 * Everything ends at `BLOOD_DURATION` regardless of when it arrived, so the
 * screen clears on one frame instead of dribbling out mark by mark after the
 * speed is already back.
 */
export function sampleSplat(seed: SplatSeed, age: number, out: SplatPose): SplatPose {
  out.x = seed.x;
  out.y = seed.y;
  out.radius = seed.radius;
  out.opacity = 0;
  out.drip = 0;

  const since = age - seed.delay;
  if (since <= 0 || age >= BLOOD_DURATION) return out;

  // How far it has run. Off its own age rather than the run's, so a mark that
  // arrived late has not already slid down the screen when it lands.
  const run = seed.run * Math.pow(since / BLOOD_DURATION, BLOOD.runPower);
  out.y = seed.y + run;
  out.drip = run;

  const landing = Math.min(1, since / BLOOD.land);
  const clearing = BLOOD.hold * BLOOD_DURATION;
  const fade = age <= clearing ? 1 : (BLOOD_DURATION - age) / (BLOOD_DURATION - clearing);
  out.opacity = seed.alpha * landing * fade;
  return out;
}
